import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRecaptcha } from "@/lib/useRecaptcha";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

// Only allow same-origin, path-only return_to values to avoid open-redirect abuse.
function safeReturnTo(raw) {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;
    if (decoded.startsWith("/login") || decoded.startsWith("/register")) return null;
    return decoded;
  } catch { return null; }
}

export default function Login() {
  const { login } = useAuth();
  const { execute } = useRecaptcha();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("return_to"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const token = await execute("login");
      const u = await login(email, password, token, remember);
      toast.success("Welcome back");
      if (returnTo && u.onboarded) {
        nav(returnTo, { replace: true });
      } else {
        nav(u.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      {/* Left: form */}
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
          </div>
          <span className="font-heading font-bold text-[15px] text-[#0A2342]">Dive Media Strategist™</span>
        </Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="dms-label mb-3">Sign in</div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-2">Back to the water.</h1>
            <p className="text-[#475569] mb-8">Pick up where you left off.</p>

            <button data-testid="google-login-button" onClick={googleLogin} type="button" className="w-full dms-btn-ghost mb-4">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/></svg>
              Continue with Google
            </button>

            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]" /></div>
              <span className="relative bg-[#F8FAFC] px-3 text-[11px] tracking-[0.2em] text-[#94A3B8] uppercase">or with email</span>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="dms-label block mb-2">Email</label>
                <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="dms-input" placeholder="you@ocean.com" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="dms-label">Password</label>
                  <Link to="/forgot-password" data-testid="login-forgot-password" className="text-[11px] font-semibold text-[#1D4E89] hover:text-[#0A2342]">
                    Forgot password?
                  </Link>
                </div>
                <input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="dms-input" placeholder="••••••••" />
              </div>
              <label className="flex items-center gap-2 text-[13px] text-[#475569] select-none cursor-pointer" data-testid="login-remember-label">
                <input
                  data-testid="login-remember-checkbox"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-[#CBD5E1] text-[#0A2342] focus:ring-[#4FD1C5]"
                />
                Keep me signed in for 30 days
              </label>
              <button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full dms-btn-primary">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <p className="mt-6 text-sm text-[#475569]">
              No account yet?{" "}
              <Link to="/register" data-testid="login-to-register" className="font-semibold text-[#1D4E89] hover:text-[#0A2342]">Create one</Link>
            </p>
            <p className="mt-4 text-[11px] text-[#94A3B8]" data-testid="recaptcha-notice">
              This site is protected by reCAPTCHA.
            </p>
          </div>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1602199926649-2e5e447bab97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxzY3ViYSUyMGRpdmVyJTIwdW5kZXJ3YXRlciUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4MTQ2MTM0MHww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2342]/60 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Doctrine</div>
          <p className="mt-3 text-xl font-heading font-semibold leading-snug">Calm confidence outperforms hype in the diving niche.</p>
        </div>
      </div>
    </div>
  );
}
